import { Instagram, MessageCircle } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { LeadCapture } from "@/components/LeadCapture";
import { INSTAGRAM_URL, WHATSAPP_COMMUNITY_URL } from "@/data/site";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SEO } from "@/components/Seo";

const FAQS = [
  {
    q: "Is everything vegan?",
    a: "Everything we make is 100% vegetarian, and most of it is fully plant-based. Where dairy appears (like curd in a ferment guide), we always suggest a vegan swap.",
  },
  {
    q: "When do deliveries start?",
    a: "Kitchens open first in Delhi NCR and Bengaluru. Waitlist members get the launch date before anyone else — plus early-access pricing.",
  },
  {
    q: "Is this medical advice?",
    a: "No. Everything we publish is educational, written in line with FSSAI advertising norms. Food supports the management of lifestyle conditions — it never replaces your doctor.",
  },
  {
    q: "What goes into the weekly letter?",
    a: "One mechanism explained in plain language, one seasonal ingredient, one recipe. Three minutes, once a week, zero spam.",
  },
];

export default function Community() {
  return (
    <div data-testid="community-page">
      <SEO
        title="Community — GenZ Eats Plants, Together | Pranivaa Kitchens"
        description="Join the Pranivaa Kitchens community on WhatsApp and Instagram — recipe wins, ferment fails, science drops and honest answers for young India."
        path="/community"
      />
      <section className="pt-28 md:pt-40 pb-16 md:pb-24 bg-beet relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: "radial-gradient(circle at 75% 20%, #C6A052 0%, transparent 45%)" }}
          aria-hidden="true"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-turmeric font-semibold mb-5">
              Community
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.06] text-white font-semibold mb-6">
              GenZ eats plants. Together.
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-white/70 max-w-2xl mb-10">
              A no-judgement corner of the internet for young India figuring out food — recipe wins,
              ferment fails, science drops and city meetups. Come as you are; leave with a better dal.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href={WHATSAPP_COMMUNITY_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="community-whatsapp-button"
                className="inline-flex items-center gap-2 rounded-full bg-leaf text-white font-semibold text-sm px-7 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
              >
                <MessageCircle size={16} /> Join on WhatsApp
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="community-instagram-link"
                className="inline-flex items-center gap-2 rounded-full border-2 border-white/25 text-white font-semibold text-sm px-7 py-4 transition-all duration-300 hover:border-turmeric hover:text-turmeric"
              >
                <Instagram size={16} /> @pranivaakitchens
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink font-semibold mb-8">
                Asked often, answered honestly.
              </h2>
              <Accordion type="single" collapsible className="w-full" data-testid="community-faq-accordion">
                {FAQS.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-[#E7E2D7]">
                    <AccordionTrigger
                      data-testid={`faq-question-${i}`}
                      className="font-serif text-lg font-semibold text-ink text-left hover:text-beet"
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-ink-soft">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="bg-paper-card border border-[#E7E2D7] rounded-3xl p-7 md:p-10 shadow-sm">
              <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-ink mb-3">
                The weekly science letter.
              </h2>
              <p className="text-sm text-ink-soft mb-7">
                One mechanism, one ingredient, one recipe. City-tagged so you only hear about launches
                near you.
              </p>
              <LeadCapture source="community" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
